// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React from 'react';
import { BreadcrumbGroup, HelpPanel, StatusIndicator, SideNavigation, Button, Modal, Box, SpaceBetween, Alert } from '@cloudscape-design/components';
import { ExternalLinkGroup } from '../commons';
import { submissionsApi } from '../../api/Submission';
import '../../styles/table-select.scss';

export const StatusComponent = ({ status }) => {
  switch (status) {
    case 'approved':
      return <StatusIndicator type="success">Approved</StatusIndicator>;
    case 'pending':
      return <StatusIndicator type="pending">Pending</StatusIndicator>;
    case 'in-review':
      return <StatusIndicator type="in-progress">In Review</StatusIndicator>;
    case 'rejected':
      return <StatusIndicator type="error">Rejected</StatusIndicator>;
    default:
      return <StatusIndicator type="stopped">Unknown</StatusIndicator>;
  }
};

// Network visibility function for RMS sync steps
const syncToRMSWithNetworkVisibility = async (submissionId) => {
  const baseUrl = 'https://wkc6hm2v1j.execute-api.eu-west-1.amazonaws.com/dev/api/v1';

  // Use the same token retrieval as the existing API client
  const token = localStorage.getItem('access_token');
  console.log('🔑 Using token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN FOUND');

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  try {
    console.log('🔄 Starting RMS sync...');

    // STEP 1: Start the main sync
    const syncResponse = await fetch(`${baseUrl}/submissions/${submissionId}/sync-to-rms`, {
      method: 'POST',
      headers
    });

    if (!syncResponse.ok) {
      throw new Error(`HTTP ${syncResponse.status}: ${syncResponse.statusText}`);
    }

    const syncResult = await syncResponse.json();
    console.log('✅ Sync started:', syncResult);

    // Get evidence groups from response
    const evidenceGroups = syncResult.evidence_groups || ['EV01', 'EV02', 'EV03', 'EV04', 'EV05'];

    // STEP 2: Process each evidence group
    for (let i = 0; i < evidenceGroups.length; i++) {
      const evNo = evidenceGroups[i];
      console.log(`📡 Processing ${evNo}...`);

      try {
        // Generate URLs
        const urlsResponse = await fetch(`${baseUrl}/submissions/${submissionId}/rms-step-generate-urls/${evNo}`, {
          method: 'POST',
          headers
        });
        if (urlsResponse.ok) await urlsResponse.json();

        // Upload files
        const uploadResponse = await fetch(`${baseUrl}/submissions/${submissionId}/rms-step-upload-files/${evNo}`, {
          method: 'POST',
          headers
        });
        if (uploadResponse.ok) await uploadResponse.json();

        // Save metadata
        const metadataResponse = await fetch(`${baseUrl}/submissions/${submissionId}/rms-step-save-metadata/${evNo}`, {
          method: 'POST',
          headers
        });
        if (metadataResponse.ok) await metadataResponse.json();

        await new Promise(resolve => setTimeout(resolve, 500));

      } catch (error) {
        console.error(`❌ Error processing ${evNo}:`, error);
      }
    }

    // Check final status
    const statusResponse = await fetch(`${baseUrl}/submissions/${submissionId}/rms-sync-status`, {
      method: 'GET',
      headers
    });
    return await statusResponse.json();

  } catch (error) {
    console.error('❌ RMS sync failed:', error);
    throw error;
  }
};

export const SubmitButton = ({ item, onSubmissionComplete }) => {
  const [isConfirmModalVisible, setIsConfirmModalVisible] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [syncStatus, setSyncStatus] = React.useState('idle'); // 'idle', 'processing', 'completed', 'failed'
  const [error, setError] = React.useState(null);
  const [canCloseModal, setCanCloseModal] = React.useState(true);

  // Resume status check on mount if potentially stuck
  React.useEffect(() => {
    if (item.is_submitted) {
      // If it looks submitted on load, check its REAL status (sync might be pending or failed)
      checkRemoteStatus();
    }
  }, [item.id]);

  const checkRemoteStatus = async () => {
    setIsSubmitting(true); // Show loading state on button while checking
    try {
      const status = await submissionsApi.getSubmissionSyncStatus(item.id);

      if (status.is_synced) {
        setSyncStatus('completed');
        setIsSubmitting(false);
      } else if (status.sync_error) {
        setSyncStatus('failed');
        setError(status.sync_error);
        setIsSubmitting(false);
      } else {
        // Still syncing - resume polling!
        setSyncStatus('processing');
        // We don't block the button here, but we start the poll loop
        startPollingLoop();
      }
    } catch (e) {
      console.error("Failed to check status", e);
      setIsSubmitting(false);
    }
  };

  const startPollingLoop = async () => {
    const checkStatus = async () => {
      try {
        const status = await submissionsApi.getSubmissionSyncStatus(item.id);

        if (status.is_synced) {
          setSyncStatus('completed');
          setIsSubmitting(false);
          setCanCloseModal(true);
          setTimeout(() => {
            setIsConfirmModalVisible(false);
            if (onSubmissionComplete) onSubmissionComplete();
          }, 2000);

        } else if (status.sync_error) {
          setSyncStatus('failed');
          setError(status.sync_error);
          setIsSubmitting(false);
          setCanCloseModal(true);
          // If modal is open, it will show error. If closed, button will show retry.

        } else {
          setTimeout(checkStatus, 3000);
        }
      } catch (pollError) {
        setTimeout(checkStatus, 5000);
      }
    };
    checkStatus();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setSyncStatus('processing');
    setCanCloseModal(false);

    try {
      // 1. Trigger Submission
      const response = await submissionsApi.submitSubmission(item.id);

      // 2. Start Polling Loop
      if (response.is_submitted) {
        startPollingLoop();
      } else {
        throw new Error("Submission did not start correctly.");
      }

    } catch (error) {
      console.error('❌ Submission request failed:', error);
      setSyncStatus('failed');
      setError(error.response?.data?.detail || error.message || 'Submission failed.');
      setCanCloseModal(true);
      setIsSubmitting(false);
    }
  };

  // Render logic based on extensive state
  if (syncStatus === 'completed' || (item.is_submitted && item.is_synced && syncStatus === 'idle')) {
    return <Button variant="normal" disabled>Submitted</Button>;
  }

  // If failed, show RETRY
  if (syncStatus === 'failed') {
    return (
      <>
        <Button variant="primary" iconName="refresh" onClick={() => setIsConfirmModalVisible(true)}>
          Retry Submission
        </Button>
        {/* Modal code below will handle the rest */}
        {renderModal()}
      </>
    );
  }

  // If processing (from resume or click), show clickable button to view progress
  if (syncStatus === 'processing') {
    return (
      <>
        <Button
          iconName="status-in-progress"
          onClick={() => setIsConfirmModalVisible(true)}
        >
          Syncing...
        </Button>
        {renderModal()}
      </>
    );
  }

  // Default: Ready to submit
  return (
    <>
      <Button
        variant="primary"
        onClick={() => setIsConfirmModalVisible(true)}
        disabled={isSubmitting}
      >
        Submit
      </Button>
      {renderModal()}
    </>
  );

  function renderModal() {
    return (
      <Modal
        onDismiss={() => canCloseModal && setIsConfirmModalVisible(false)}
        visible={isConfirmModalVisible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              {syncStatus === 'failed' && (
                <Button variant="link" onClick={() => setIsConfirmModalVisible(false)}>
                  Close
                </Button>
              )}
              {syncStatus !== 'processing' && syncStatus !== 'failed' && (
                <>
                  <Button variant="link" onClick={() => setIsConfirmModalVisible(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
                    Confirm Submit
                  </Button>
                </>
              )}
              {/* Provide a Retry button inside modal if failed */}
              {syncStatus === 'failed' && (
                <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
                  Retry Now
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="Confirm Submission"
      >
        <SpaceBetween size="m">
          {syncStatus === 'processing' && (
            <Alert type="info">🔄 Syncing files to RMS... Please wait.</Alert>
          )}

          {syncStatus === 'completed' && (
            <Alert type="success">✅ Submission successful! All files synced.</Alert>
          )}

          {syncStatus === 'failed' && (
            <Alert type="error">
              ❌ {error}
              <br />
              <small>The submission was accepted but file sync failed in the background.</small>
            </Alert>
          )}

          {syncStatus === 'idle' && (
            <p>Are you sure you want to submit Application-{item.id}? This action cannot be undone.</p>
          )}
        </SpaceBetween>
      </Modal>
    );
  }
};

const header = { text: 'User: University of Bahrain', href: '#/' };

const items = [
  { type: 'link', text: 'Home', href: '#/dashboard' },
  { type: 'link', text: 'My Submissions', href: '#/instances' },
  // { type: 'link', text: 'Clusters', href: '#/clusters' },
  // { type: 'link', text: 'Performance Insights', href: '#/perfomance' },
  // { type: 'link', text: 'Snapshots', href: '#/usage' },
  // { type: 'link', text: 'Reserved instances', href: '#/reservedinstances' },
  { type: 'divider' },
  { type: 'link', text: 'Account settings', href: '/profile-form.html' },
  // { type: 'link', text: 'Parameter groups', href: '#/paramgroups' },
  // { type: 'link', text: 'Option groups', href: '#/optiongroups' },
  // { type: 'link', text: 'Events', href: '#/event' },
  // { type: 'link', text: 'Event subscriptions', href: '#/eventsub' },
];

export class Navigation extends React.Component {
  onFollowHandler(ev) {
    // keep the locked href for our demo pages
    ev.preventDefault();
  }

  render() {
    return (
      <SideNavigation
        items={items}
        header={header}
        activeHref={this.props.activeHref}
        onFollow={this.onFollowHandler.bind(this)}
      />
    );
  }
}

export const Breadcrumbs = () => (
  <BreadcrumbGroup
    expandAriaLabel="Show path"
    ariaLabel="Breadcrumbs"
    items={[
      { text: 'Home', href: '#' },
      { text: 'Submissions', href: '#' },
    ]}
  />
);

export const ToolsContent = () => (
  <HelpPanel
    header={<h2>Instances</h2>}
    footer={
      <ExternalLinkGroup
        items={[
          {
            href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Overview.DBInstance.html',
            text: 'Amazon RDS database instances',
          },
          {
            href: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Overview.DBInstance.Status.html',
            text: 'DB instance status',
          },
        ]}
      />
    }
  >
    <p>
      View your current DB instances and related information such as the engine, status, connections, class, and more.
      You can filter your instances by engine or class. To drill down even further into the details, choose the name of
      an individual instance.
    </p>
    <p>
      The status of a DB instance indicates the health of the DB instance. When you first create a DB instance, it has a
      status of <b>Creating</b> until the instance is ready to use. When the state changes to <b>Available</b>, you can
      connect to the instance.
    </p>
  </HelpPanel>
);